/**
 * RN-13 / RN-14 — trava do campo "Será possível reenviar a resposta?" no
 * editor de etapa (`PhaseSheet`).
 *
 * - RN-14 — etapas de **Currículo** e **Conteúdo** sempre podem ser refeitas →
 *   o campo fica desabilitado e marcado em "Sim".
 * - RN-13 — etapas de **Teste** cujo instrumento não aceita reenvio → o campo
 *   fica desabilitado e marcado em "Não". Quem decide é o Core, pelo
 *   `canResubmit` que vem no catálogo.
 *
 * **EU-764 — a dívida do match por nome foi quitada.** Até aqui, "este teste
 * não pode ser refeito" era decidido comparando o RÓTULO da opção com uma
 * lista de nomes escrita neste arquivo. Renomear um preset no banco quebrava
 * o match em silêncio: o campo destravava e o consultor podia marcar "Sim"
 * num teste que a regra diz que nunca se refaz, sem erro nenhum aparecer.
 *
 * O Core já mandava a resposta certa — `canResubmit` por item no catálogo,
 * decidido pelo `kind` — e o valor era descartado na projeção do container.
 * Agora é ele que decide, e o nome do teste deixou de importar.
 */

export interface ResubmitLock {
  /** Quando `true`, o radio fica desabilitado e fixado em `value`. */
  locked: boolean;
  /** Valor forçado quando `locked`; `null` quando o consultor escolhe livre. */
  value: 'sim' | 'nao' | null;
  /** Texto curto explicando por que o campo está travado (hint sob o campo). */
  reason: string | null;
}

export const UNLOCKED_RESUBMIT: ResubmitLock = { locked: false, value: null, reason: null };

interface CatalogueOption {
  value: string;
  label: string;
  /**
   * Política de reenvio do instrumento, vinda do Core: `false` trava em "Não",
   * `null` deixa o consultor escolher. `undefined` = catálogo antigo, sem o
   * flag — nesse caso o campo fica livre, que é o comportamento de antes.
   */
  canResubmit?: boolean | null;
}

/**
 * Decide se o campo "Será possível reenviar a resposta?" deve ficar travado,
 * dado o tipo da etapa e (para Teste) o teste selecionado.
 *
 * `testOptions` é o catálogo carregado pelo container. O que interessa dele
 * aqui é o `canResubmit` do teste selecionado — o rótulo não participa mais da
 * decisão.
 */
export function computeResubmitLock(
  type: string,
  testId: string,
  testOptions: readonly CatalogueOption[]
): ResubmitLock {
  // RN-14 — Currículo e Conteúdo sempre podem ser refeitos.
  if (type === 'curriculo' || type === 'conteudo') {
    return {
      locked: true,
      value: 'sim',
      reason: 'Etapas de Currículo e Conteúdo sempre podem ser refeitas.',
    };
  }
  // Teste externo (incl. Fator H / Kolab) — quem confirma a conclusão é a
  // plataforma externa (webhook/consulta), não uma resposta enviada aqui;
  // "reenviar a resposta" não se aplica. Travado em "Não" para o upsert do
  // resultado nunca esbarrar em regra de reenvio.
  if (type === 'teste-externo' || type === 'fator-h') {
    return {
      locked: true,
      value: 'nao',
      reason: 'A conclusão de testes externos é confirmada pela própria plataforma.',
    };
  }
  // RN-13 — o próprio instrumento diz se aceita reenvio (EU-764).
  if (type === 'teste' && testId) {
    const option = testOptions.find((o) => o.value === testId);
    if (option?.canResubmit === false) {
      return {
        locked: true,
        value: 'nao',
        reason: 'Este teste não pode ser refeito pelo candidato.',
      };
    }
  }
  return UNLOCKED_RESUBMIT;
}
